import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getItems } from "../../api/items";
import { useDashboardAccess } from "../DashboardAccessProvider";
import { getExtensionItems } from "../extensionItems";
import { transportQueryKey } from "../nativeDashboardsAccess";
import { getAggregate } from "../useAggregate";
import { DrillDownColumn, DrillDownConfig, QueryDrillDownConfig } from "../types";
import { dashboardTableSx, ROW_HOVER_BACKGROUND, ZEBRA_ROW_BACKGROUND } from "./tableStyles";
import { CustomChip } from "../../components/chips/CustomChip";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const cellValue = (column: DrillDownColumn, item: any) => {
    const value = column.valueFor ? column.valueFor(item) : item[column.key];
    if (value === null || value === undefined || value === "") return "—";
    return value;
};

// Célula: chip (cor + label das definições do campo Directus, igual à grid) quando a coluna tem chipField;
// caso contrário texto normal.
const renderCellContent = (column: DrillDownColumn, item: any) => {
    if (!column.chipField) {
        const text = cellValue(column, item);
        return text;
    }
    const rawValue = item[column.key];
    if (rawValue === null || rawValue === undefined || rawValue === "") return "—";
    const chip = <CustomChip field={column.chipField} label={String(rawValue)} extraOptions={{ style: { size: "small" } }} />;
    return chip;
};

const sortRowsByColumn = (rows: Array<any>, columns: Array<DrillDownColumn>, sortKey: string, isDescending: boolean) => {
    const column = columns.find((candidate) => candidate.key === sortKey);
    const rawOrFormatted = (row: any) => (row[sortKey] !== undefined ? row[sortKey] : column?.valueFor ? column.valueFor(row) : null);
    const sorted = [...rows].sort((firstRow, secondRow) => {
        const firstValue = rawOrFormatted(firstRow);
        const secondValue = rawOrFormatted(secondRow);
        if (firstValue === secondValue) return 0;
        if (firstValue === null || firstValue === undefined) return 1;
        if (secondValue === null || secondValue === undefined) return -1;
        const comparison = firstValue < secondValue ? -1 : 1;
        return isDescending ? -comparison : comparison;
    });
    return sorted;
};

type DrillDownDialogProps = {
    config: DrillDownConfig;
    onClose: () => void;
};

export const DrillDownDialog = ({ config, onClose }: DrillDownDialogProps) => {
    const access = useDashboardAccess();
    const [page, setPage] = useState(0);
    const initialPageSize = config.kind === "rows" && config.defaultPageSize ? config.defaultPageSize : PAGE_SIZE_OPTIONS[0];
    const [pageSize, setPageSize] = useState(initialPageSize);
    const isQueryMode = config.kind === "query";
    const queryConfig = isQueryMode ? (config as QueryDrillDownConfig) : null;
    const [sortKey, setSortKey] = useState(isQueryMode ? queryConfig!.defaultSort : config.defaultSortKey || config.columns[0].key);
    const isDescending = sortKey.startsWith("-");
    const activeSortField = isDescending ? sortKey.slice(1) : sortKey;

    // Em dashboards data_scope=global as linhas vêm da rota /drill da extensão (check de drill_roles
    // no servidor); em user-scope continuam por /items com o token (permissões) do utilizador.
    const usesExtensionTransport = access.transport === "extension";
    const drillTransportKey = transportQueryKey(access.transport, access.dashboardKey);

    const itemsResult = useQuery({
        queryKey: ["drilldown", drillTransportKey, queryConfig?.entity, JSON.stringify(queryConfig?.filter), sortKey, page, pageSize],
        queryFn: () => {
            const itemsOptions = { filter: queryConfig!.filter, sort: sortKey, page, pageSize, withRowCount: true };
            if (usesExtensionTransport) {
                return getExtensionItems(access.dashboardKey, "drill", queryConfig!.entity, queryConfig!.fieldsQuery, itemsOptions);
            }
            return getItems(queryConfig!.entity, queryConfig!.fieldsQuery, itemsOptions);
        },
        enabled: isQueryMode,
        keepPreviousData: true,
    });

    const headerAggregateResult = useQuery({
        queryKey: [
            "drilldown-header-aggregate",
            drillTransportKey,
            queryConfig?.entity,
            JSON.stringify(queryConfig?.filter),
            JSON.stringify(queryConfig?.headerAggregate?.aggregate),
        ],
        queryFn: () =>
            getAggregate(
                { entity: queryConfig!.entity, aggregate: queryConfig!.headerAggregate!.aggregate, dataScope: "global" },
                queryConfig!.filter,
                usesExtensionTransport ? { dashboardKey: access.dashboardKey, transport: "extension" } : undefined
            ),
        enabled: isQueryMode && Boolean(queryConfig?.headerAggregate),
    });

    const toggleSort = (column: DrillDownColumn) => {
        const field = isQueryMode ? column.sortField : column.key;
        if (!field) return;
        setPage(0);
        setSortKey((current) => {
            const currentField = current.startsWith("-") ? current.slice(1) : current;
            if (currentField === field) return current.startsWith("-") ? field : `-${field}`;
            return field;
        });
    };

    const allRows = isQueryMode
        ? itemsResult.data?.items || []
        : sortRowsByColumn(config.kind === "rows" ? config.rows : [], config.columns, activeSortField, isDescending);
    const visibleRows = isQueryMode ? allRows : allRows.slice(page * pageSize, page * pageSize + pageSize);
    const totalCount = isQueryMode
        ? Number(itemsResult.data?.metadata?.filter_count ?? itemsResult.data?.metadata?.total_count ?? 0)
        : allRows.length;

    const headerAggregateRaw = headerAggregateResult.data?.[0];
    const headerAggregateValue =
        queryConfig?.headerAggregate && headerAggregateRaw
            ? Number(Object.values(headerAggregateRaw.sum || headerAggregateRaw)[0] || 0)
            : null;

    const openRow = (row: any) => {
        const route = config.rowRoute ? config.rowRoute(row) : null;
        if (!route) return;
        window.open(route, "_blank", "noopener");
    };

    const isLoading = isQueryMode && itemsResult.isLoading;
    const isError = isQueryMode && itemsResult.isError;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <Box>
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            <Typography variant="h6" component="span">
                                {config.title}
                            </Typography>
                            <Chip size="small" label={`${totalCount.toLocaleString("pt-PT")} registos`} />
                            {queryConfig?.headerAggregate && headerAggregateValue !== null && (
                                <Chip size="small" color="primary" variant="outlined" label={`${queryConfig.headerAggregate.label}: ${queryConfig.headerAggregate.format(headerAggregateValue)}`} />
                            )}
                            {config.kind === "rows" &&
                                config.headerChips?.map((chipLabel) => (
                                    <Chip key={chipLabel} size="small" color="primary" variant="outlined" label={chipLabel} />
                                ))}
                        </Box>
                        {config.queryDescription && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                                {config.queryDescription}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {isLoading && <Skeleton variant="rounded" height={300} />}
                {!isLoading && isError && (
                    <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                        Erro ao carregar os dados.
                    </Typography>
                )}
                {!isLoading && !isError && (
                    <>
                        <TableContainer sx={{ maxHeight: "60vh" }}>
                            <Table size="small" stickyHeader sx={dashboardTableSx}>
                                <TableHead>
                                    <TableRow>
                                        {config.columns.map((column) => {
                                            const sortField = isQueryMode ? column.sortField : column.key;
                                            return (
                                                <TableCell key={column.key} align={column.isNumeric ? "right" : "left"} sx={{ fontWeight: 600 }}>
                                                    {sortField ? (
                                                        <TableSortLabel
                                                            active={activeSortField === sortField}
                                                            direction={activeSortField === sortField && isDescending ? "desc" : "asc"}
                                                            onClick={() => toggleSort(column)}
                                                        >
                                                            {column.label}
                                                        </TableSortLabel>
                                                    ) : (
                                                        column.label
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visibleRows.map((row: any, rowIndex: number) => {
                                        const hasRoute = Boolean(config.rowRoute && config.rowRoute(row));
                                        const rowSx = config.kind === "rows" && config.rowSx ? config.rowSx(row) : undefined;
                                        return (
                                            <TableRow
                                                key={row.id ?? rowIndex}
                                                onClick={() => openRow(row)}
                                                sx={{
                                                    ...(rowIndex % 2 === 1 ? { backgroundColor: ZEBRA_ROW_BACKGROUND } : {}),
                                                    ...(hasRoute ? { cursor: "pointer" } : {}),
                                                    ...rowSx,
                                                    "&:hover": { backgroundColor: ROW_HOVER_BACKGROUND },
                                                }}
                                            >
                                                {config.columns.map((column) => (
                                                    <TableCell key={column.key} align={column.isNumeric ? "right" : "left"}>
                                                        {renderCellContent(column, row)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                    {visibleRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={config.columns.length} align="center">
                                                0 registos no período/filtros selecionados.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(event) => {
                                setPageSize(Number(event.target.value));
                                setPage(0);
                            }}
                            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                            labelRowsPerPage="Linhas por página"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count.toLocaleString("pt-PT")}`}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
